import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiPlus,
  FiMinus,
  FiTrash2,
  FiMapPin,
  FiClock,
  FiEdit2,
  FiCreditCard,
} from 'react-icons/fi';
import { IoRestaurantOutline, IoBicycleOutline } from 'react-icons/io5';
import { Card, LoaderComponent } from '../../components/ui';
import { api } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts';
import { useFoodCart } from '../../contexts/FoodCartContext';
import walletService from '../../services/wallet.service';
import type { SavedCardSummary } from '../../types/wallet.types';

type OrderType = 'DELIVERY' | 'TAKEAWAY';

const FoodCart: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const foodCart = useFoodCart();

  const cart = foodCart.cart;
  const restaurant = foodCart.restaurant;
  const [orderType, setOrderType] = useState<OrderType>('DELIVERY');
  // Métodos: 'SISP' (cartão novo, página da SISP), 'token:<id>' (cartão guardado, cobra
  // na hora) e 'WALLET' (só quando a API diz que está activa).
  const [metodo, setMetodo] = useState<string>('SISP');
  const [savedCards, setSavedCards] = useState<SavedCardSummary[]>([]);
  const [walletAtiva, setWalletAtiva] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  // Ponto de entrega (localização do browser) e a taxa calculada para ele — no modo
  // «por distância» a taxa depende do ponto (Admin › Pagali Food › Taxa de entrega, 26/10)
  const [ponto, setPonto] = useState<{ lat: number; lng: number } | null>(null);
  const [cotacao, setCotacao] = useState<{ taxa: number; km: number | null; modo: string } | null>(null);
  const [erroPonto, setErroPonto] = useState<string | null>(null);
  const [aLocalizar, setALocalizar] = useState(false);
  // Erro da morada junto ao campo: o aviso flutuante passava despercebido.
  const [erroMorada, setErroMorada] = useState(false);
  const moradaRef = useRef<HTMLInputElement>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  // Termos do Pagali Food (o texto vem do servidor; o pedido grava a versão aceite)
  const [termos, setTermos] = useState<{ versao: string; titulo: string; secoes: Array<{ titulo: string; texto: string }> } | null>(null);
  const [aceitaTermos, setAceitaTermos] = useState(false);
  const [verTermos, setVerTermos] = useState(false);

  useEffect(() => {
    let vivo = true;
    walletService.getCards()
      .then((cards) => {
        if (!vivo) return;
        setSavedCards(cards);
        const preferido = cards.find((c) => c.isDefault) || cards[0];
        if (preferido) setMetodo(`token:${preferido.id}`);
      })
      .catch(() => { if (vivo) setSavedCards([]); });
    api.get('/food/termos')
      .then((r) => { if (vivo) setTermos(r.data?.data ?? null); })
      .catch(() => undefined);
    api.get('/food/payment-methods')
      .then((r) => { if (vivo) setWalletAtiva(!!r.data?.data?.wallet); })
      .catch(() => { if (vivo) setWalletAtiva(false); });
    return () => { vivo = false; };
  }, []);

  const updateQuantity = (itemId: number, delta: number) => {
    foodCart.updateQuantity(itemId, delta);
  };

  const removeItem = (itemId: number) => {
    foodCart.updateQuantity(itemId, -999);
  };

  const subtotal = foodCart.cartTotal;
  const deliveryFee = orderType === 'DELIVERY' ? Number(cotacao?.taxa ?? restaurant?.deliveryFee ?? 0) : 0;
  // Sem descontos no browser: o antigo «PAGALI10» só baixava o total no ecrã — o
  // servidor cobrava o preço completo.
  const total = Number(subtotal) + Number(deliveryFee);

  useEffect(() => {
    if (!restaurant?.id || orderType !== 'DELIVERY') return;
    const q = ponto ? `?lat=${ponto.lat}&lng=${ponto.lng}` : '';
    api.get(`/food/restaurants/${restaurant.id}/taxa-entrega${q}`)
      .then((r) => { setCotacao(r.data.data); setErroPonto(null); })
      .catch((e) => {
        const d = e?.response?.data;
        setCotacao(null);
        // SEM_PONTO só se mostra depois de o cliente tentar; FORA_DE_AREA mostra-se logo
        if (d?.code === 'FORA_DE_AREA' || (d?.code === 'SEM_PONTO' && ponto)) setErroPonto(d.error);
      });
  }, [restaurant?.id, orderType, ponto]);

  const usarLocalizacao = () => {
    if (!navigator.geolocation) { setErroPonto('Este browser não dá a localização.'); return; }
    setALocalizar(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPonto({ lat: p.coords.latitude, lng: p.coords.longitude }); setALocalizar(false); },
      () => { setErroPonto('Não foi possível obter a localização. Autorize-a no browser ou faça o pedido na app.'); setALocalizar(false); },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const formatPrice = (price: number | string) => `${Number(price || 0).toFixed(0)}$`;

  const handleCheckout = async () => {
    if (cart.length === 0) {
      showError('Carrinho vazio');
      return;
    }

    if (orderType === 'DELIVERY' && !deliveryAddress.trim()) {
      setErroMorada(true);
      moradaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      moradaRef.current?.focus();
      showError('Indique a morada de entrega');
      return;
    }

    if (!aceitaTermos) {
      showError('Para continuar, aceite os Termos do Pagali Food');
      return;
    }

    try {
      setLoading(true);

      const orderData = {
        termosVersao: termos?.versao,
        restaurantId: restaurant?.id,
        ...(orderType === 'DELIVERY' && ponto ? { deliveryLat: ponto.lat, deliveryLng: ponto.lng } : {}),
        type: orderType,
        items: cart.map((item) => ({
          menuItemId: item.id,
          quantity: item.quantity,
          notes: '',
        })),
        deliveryAddress: orderType === 'DELIVERY' ? deliveryAddress : undefined,
        notes,
      };

      const response = await api.post('/food/orders', orderData);

      if (response.data.success) {
        const pedido = response.data.data;
        if (!pedido.paymentRequired) {
          foodCart.clearCart();
          showSuccess('Pedido realizado com sucesso!');
          navigate('/food/orders', { state: { newOrderId: pedido.id } });
          return;
        }
        // Entrega/levantamento: paga-se já; o restaurante só recebe depois de pago.
        if (metodo.startsWith('token:')) {
          // Cartão guardado: a SISP cobra na hora, sem página.
          await api.post(`/food/orders/${pedido.id}/pay`, { metodo: 'TOKEN', token_id: metodo.slice('token:'.length) });
          foodCart.clearCart();
          showSuccess('Pagamento confirmado! O restaurante já recebeu o seu pedido.');
          navigate('/food/orders', { state: { newOrderId: pedido.id } });
          return;
        }
        if (metodo === 'WALLET') {
          await api.post(`/food/orders/${pedido.id}/pay`, { metodo: 'WALLET' });
          foodCart.clearCart();
          showSuccess('Pagamento confirmado! O restaurante já recebeu o seu pedido.');
          navigate('/food/orders', { state: { newOrderId: pedido.id } });
          return;
        }
        // Cartão novo: a página da SISP abre na PRÓPRIA página (não numa janela embutida).
        // Dentro de um iframe o 3-D Secure expirava ao fim de ~11 s («UserCancelled») antes
        // de se acabar de pôr o cartão. No fim, a SISP devolve a /food/orders com o resultado.
        const pay = await api.post(`/food/orders/${pedido.id}/pay`, {
          metodo: 'SISP',
          returnUrl: `${window.location.origin}/food/orders?pedido=${pedido.id}`,
        });
        window.location.assign(pay.data.data.paymentUrl);
      }
    } catch (error: any) {
      console.error('Error creating order:', error);
      showError(
        error.response?.data?.error || 'Erro ao realizar pedido'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <IoRestaurantOutline className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Carrinho vazio</p>
          <button
            onClick={() => navigate('/food')}
            className="mt-4 text-pagali-purple font-medium"
          >
            Explorar restaurantes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <div className="bg-white px-4 py-4 flex items-center gap-4 border-b">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <FiArrowLeft />
        </button>
        <div>
          <h1 className="font-semibold text-gray-800">Carrinho</h1>
          <p className="text-sm text-gray-500">{restaurant.name}</p>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Order Type */}
        <Card>
          <h3 className="font-medium text-gray-800 mb-3">Tipo de Pedido</h3>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setOrderType('DELIVERY')}
              className={`py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                orderType === 'DELIVERY'
                  ? 'bg-pagali-purple text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <IoBicycleOutline />
              Entrega
            </button>
            <button
              onClick={() => setOrderType('TAKEAWAY')}
              className={`py-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                orderType === 'TAKEAWAY'
                  ? 'bg-pagali-purple text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              <IoRestaurantOutline />
              Levantar
            </button>
          </div>
        </Card>

        {/* Delivery Address */}
        {orderType === 'DELIVERY' && (
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-gray-800">Endereço de Entrega</h3>
              <button
                type="button"
                onClick={() => moradaRef.current?.focus()}
                className="text-pagali-purple text-sm flex items-center gap-1"
              >
                <FiEdit2 className="text-xs" /> Editar
              </button>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-pagali-purple/10 rounded-full flex items-center justify-center text-pagali-purple">
                <FiMapPin />
              </div>
              <div className="flex-1">
                <input
                  ref={moradaRef}
                  type="text"
                  value={deliveryAddress}
                  onChange={(e) => { setDeliveryAddress(e.target.value); if (e.target.value.trim()) setErroMorada(false); }}
                  placeholder="Rua, número, zona e um ponto de referência"
                  aria-invalid={erroMorada}
                  aria-describedby={erroMorada ? 'erro-morada' : undefined}
                  className={`w-full rounded-lg border p-2 text-sm focus:outline-none focus:ring-2 ${
                    erroMorada
                      ? 'border-pagali-error bg-pagali-error-light focus:ring-pagali-error/30'
                      : 'border-gray-200 focus:ring-pagali-purple/20'
                  }`}
                />
                {erroMorada && (
                  <p id="erro-morada" className="mt-1 text-sm font-medium text-pagali-error">
                    Indique a morada de entrega para o estafeta saber onde levar o pedido.
                  </p>
                )}
                <button type="button" onClick={usarLocalizacao} disabled={aLocalizar}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-pagali-purple hover:underline disabled:opacity-60">
                  <FiMapPin /> {aLocalizar ? 'A obter a localização…' : ponto ? 'Localização marcada — actualizar' : 'Usar a minha localização como ponto de entrega'}
                </button>
                {cotacao?.km != null && (
                  <p className="mt-1 text-sm text-gray-600">
                    Entrega a {cotacao.km.toLocaleString('pt-PT')} km · {formatPrice(cotacao.taxa)}
                  </p>
                )}
                {erroPonto && <p className="mt-1 text-sm font-medium text-pagali-error">{erroPonto}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
              <FiClock />
              <span>
                Tempo estimado: {restaurant.deliveryTimeMin || 30}-
                {(restaurant.deliveryTimeMin || 30) + 15} min
              </span>
            </div>
          </Card>
        )}

        {/* Cart Items */}
        <Card>
          <h3 className="font-medium text-gray-800 mb-3">Itens do Pedido</h3>
          <div className="space-y-4">
            {cart.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <IoRestaurantOutline />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-800 truncate">{item.name}</h4>
                  <p className="text-sm text-pagali-purple font-medium">
                    {formatPrice(item.price)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(item.id, -1)}
                    className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600"
                  >
                    <FiMinus className="text-sm" />
                  </button>
                  <span className="w-6 text-center font-medium">{item.quantity}</span>
                  <button
                    onClick={() => updateQuantity(item.id, 1)}
                    className="w-8 h-8 bg-pagali-purple rounded-full flex items-center justify-center text-white"
                  >
                    <FiPlus className="text-sm" />
                  </button>
                  <button
                    onClick={() => removeItem(item.id)}
                    className="w-8 h-8 text-red-500 flex items-center justify-center"
                  >
                    <FiTrash2 className="text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Notes */}
        <Card>
          <h3 className="font-medium text-gray-800 mb-2">Observações</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Ex: Sem cebola, bem passado..."
            className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none h-20"
          />
        </Card>

        {/* Payment Method */}
        <Card>
          <h3 className="font-medium text-gray-800 mb-3">Forma de Pagamento</h3>
          <div className="space-y-2">
            {[
              // Cartões guardados primeiro: quem tem um quer usá-lo.
              ...savedCards.map((c) => ({
                value: `token:${c.id}`,
                titulo: c.alias || `${c.cardType || 'Cartão'} •••• ${c.lastFourDigits}`,
                sub: 'Cartão guardado · pagamento imediato',
              })),
              { value: 'SISP', titulo: savedCards.length ? 'Outro cartão' : 'Cartão Vinti4 / Visa / Mastercard', sub: 'Pagamento seguro pela SISP' },
              ...(walletAtiva ? [{ value: 'WALLET', titulo: 'Carteira Pagali', sub: 'Pagar com o saldo da carteira' }] : []),
            ].map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMetodo(m.value)}
                className={`flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-colors ${
                  metodo === m.value ? 'border-pagali-purple bg-pagali-purple/10' : 'border-pagali-gray-200 bg-white hover:border-pagali-gray-300'
                }`}
              >
                <FiCreditCard className={`text-xl ${metodo === m.value ? 'text-pagali-purple' : 'text-pagali-gray-400'}`} />
                <span>
                  <span className="block font-medium text-pagali-gray-800">{m.titulo}</span>
                  <span className="block text-xs text-pagali-gray-500">{m.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </Card>

        {/* Termos do Pagali Food */}
        <Card>
          <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-pagali-purple" checked={aceitaTermos}
              onChange={(e) => setAceitaTermos(e.target.checked)} />
            <span>
              Li e aceito os{' '}
              <button type="button" className="font-semibold text-pagali-purple underline" onClick={() => setVerTermos(true)}>
                {termos?.titulo ?? 'Termos do Pagali Food'}
              </button>
              . A taxa de entrega não é devolvida se a entrega falhar por motivo do cliente.
            </span>
          </label>
        </Card>

        {verTermos && termos && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 md:items-center" onClick={() => setVerTermos(false)}>
            <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 md:rounded-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-gray-800">{termos.titulo}</h3>
              <p className="mb-4 text-xs text-gray-500">Versão {termos.versao}</p>
              {termos.secoes.map((sec) => (
                <div key={sec.titulo} className="mb-3">
                  <p className="font-semibold text-gray-800">{sec.titulo}</p>
                  <p className="text-sm leading-relaxed text-gray-600">{sec.texto}</p>
                </div>
              ))}
              <button className="mt-2 w-full rounded-xl bg-pagali-purple py-3 font-semibold text-white"
                onClick={() => { setAceitaTermos(true); setVerTermos(false); }}>
                Li e aceito
              </button>
            </div>
          </div>
        )}

        {/* Order Summary */}
        <Card>
          <h3 className="font-medium text-gray-800 mb-3">Resumo</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            {orderType === 'DELIVERY' && (
              <div className="flex justify-between text-gray-600">
                <span>Taxa de Entrega</span>
                <span>{deliveryFee === 0 ? 'Grátis' : formatPrice(deliveryFee)}</span>
              </div>
            )}
            <div className="border-t pt-2 flex justify-between font-semibold text-gray-800">
              <span>Total</span>
              <span className="text-pagali-purple">{formatPrice(total)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Checkout Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <button
          onClick={handleCheckout}
          disabled={loading || cart.length === 0}
          className="w-full bg-pagali-purple text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-pagali-purple-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <LoaderComponent />
          ) : (
            <>
              Confirmar Pedido
              <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                {formatPrice(total)}
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FoodCart;
